import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Series, InsertSeries, supabase } from "../../lib/supabase";
import { Listbox } from "../ui/Listbox";

interface SeriesFormProps {
  defaultValues?: Series;
  onSubmit: (data: InsertSeries | Series) => void;
  isLoading: boolean;
  onCancel?: () => void;
}

const SeriesForm: React.FC<SeriesFormProps> = ({
  defaultValues,
  onSubmit,
  isLoading,
  onCancel,
}) => {
  const [availableSeries, setAvailableSeries] = useState<
    { id: string; title: string; code: string }[]
  >([]);
  const [isLoadingSeries, setIsLoadingSeries] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InsertSeries>({
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      code: defaultValues?.code || "",
      prerequisites: defaultValues?.prerequisites || [],
      type: defaultValues?.type || "series",
    },
  });

  useEffect(() => {
    const fetchSeries = async () => {
      setIsLoadingSeries(true);
      try {
        const { data, error } = await supabase
          .from("series")
          .select("id, title, code")
          .order("code", { ascending: true });

        if (error) throw error;

        // Filter out current series if editing
        const filteredSeries =
          data?.filter((s) => s.id !== defaultValues?.id) || [];
        setAvailableSeries(filteredSeries);
      } catch (error) {
        console.error("Error fetching series:", error);
      } finally {
        setIsLoadingSeries(false);
      }
    };

    fetchSeries();
  }, [defaultValues?.id]);

  const handleFormSubmit = (data: InsertSeries) => {
    const formattedData: InsertSeries = {
      ...data,
      prerequisites: data.prerequisites || [],
    };
    onSubmit(formattedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label htmlFor="title" className="label">
              Title <span className="text-error-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              className={`input ${
                errors.title
                  ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                  : ""
              }`}
              {...register("title", { required: "Title is required" })}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-error-500">
                {errors.title.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="code" className="label">
              Code <span className="text-error-500">*</span>
            </label>
            <input
              id="code"
              type="text"
              placeholder="e.g., MUSIC, ROBOTICS, INTRO"
              className={`input ${
                errors.code
                  ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                  : ""
              }`}
              {...register("code")}
            />
            {errors.code && (
              <p className="mt-1 text-sm text-error-500">
                {errors.code.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="type" className="label">
              Program Type <span className="text-error-500">*</span>
            </label>
            <select
              id="type"
              className="input"
              {...register("type", { required: "Program type is required" })}
            >
              <option value="series">Series (Multi-session course)</option>
              <option value="event">Event (Single-day)</option>
              <option value="camp">Camp (Multi-day)</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-error-500">
                {errors.type.message}
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="description" className="label">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              className="input"
              {...register("description")}
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="prerequisites" className="label">
              Prerequisites
            </label>
            <Controller
              name="prerequisites"
              control={control}
              render={({ field }) => (
                <Listbox
                  multiple
                  options={availableSeries.map((series) => ({
                    value: series.id,
                    label: `${series.code}: ${series.title}`,
                  }))}
                  value={field.value || []}
                  onChange={field.onChange}
                  disabled={isLoadingSeries}
                />
              )}
            />
            <p className="mt-1 text-sm text-neutral-500">
              Click the dropdown to choose required series. Selected items will
              show checkmarks. Multiple selections are allowed.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-outline">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className={`btn btn-primary ${
            isLoading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isLoading ? (
            <>
              <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </form>
  );
};

export default SeriesForm;
